import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, office } from "@/lib/api";
import { session, type OfficeUser } from "@/lib/session";
import { useTill } from "@/lib/till-context";
import { mediaUrl } from "@/lib/media";
import { Button, Field, Input, Spinner, PasswordInput } from "@/components/ui";

type JoinInfo = { invite: { name: string; login: string; role: string; roleLabel: string; invitedBy: string }; store: { name: string } };

/** An invite link (office.<domain>/join/<code>): the invited person sets a password and is signed in. */
export function Join({ code }: { code: string }) {
  const { store } = useTill();
  const q = useQuery({ queryKey: ["join", code], queryFn: () => api<JoinInfo>(office(`/join/${encodeURIComponent(code)}`)), retry: false });
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Use at least 8 characters");
    if (pw !== again) return setErr("The two passwords aren't the same");
    setBusy(true);
    setErr("");
    try {
      const r = await api<{ token: string; user: OfficeUser }>(office(`/join/${encodeURIComponent(code)}`), { body: { password: pw } });
      session.signIn(r.token, r.user);
      window.history.replaceState(null, "", "/");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't finish joining");
    } finally {
      setBusy(false);
    }
  };
  const logo = store?.settings?.logo;
  return (
    <div className="grid min-h-full place-items-center bg-graphite px-4 py-10">
      <div className="flex w-full max-w-[440px] flex-col gap-5 bg-white p-7">
        {logo ? <img src={mediaUrl(logo, 600)} alt={store?.storeName ?? ""} className="h-10 w-auto self-start object-contain" /> : <b className="text-lg">{store?.storeName ?? "Back office"}</b>}
        {q.isLoading ? (
          <Spinner />
        ) : !q.data ? (
          <div className="flex flex-col gap-2">
            <b className="text-xl">This invite doesn't work</b>
            <p className="text-sm text-slate-600">{q.error instanceof Error ? q.error.message : "Ask the owner for a new link."}</p>
            <a href="/" className="text-sm font-semibold">
              Go to sign in
            </a>
          </div>
        ) : (
          <form onSubmit={join} className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-black">Welcome, {q.data.invite.name}</h1>
              <p className="mt-1 text-sm text-slate-600">
                {q.data.invite.invitedBy} added you to the {q.data.store.name} back office as <b>{q.data.invite.roleLabel.toLowerCase()}</b>. Choose a password to finish.
              </p>
            </div>
            <Field label="You'll sign in with">
              <Input value={q.data.invite.login} readOnly className="bg-slate-50 font-mono" />
            </Field>
            <Field label="New password" hint="At least 8 characters">
              <PasswordInput autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
            </Field>
            <Field label="Same password again">
              <PasswordInput autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
            </Field>
            {err ? <p className="bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">{err}</p> : null}
            <Button tone="primary" size="lg" type="submit" disabled={busy}>
              {busy ? "Setting up…" : "Set password and open the back office"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
